import { useI18n, type I18nContextValue } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { EmptyState } from '@ecom/ui-kit';
import { type ReactElement } from 'react';

import { describeActivity, formatActivityTime } from '../activity';
import { useRecentActivityQuery } from '../api';
import type { ActivityEvent } from '../types';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';

/**
 * « Activité récente » — what really happened in this shop lately: order status changes and stock movements, read
 * from the two journals that already exist (`order_status_history`, `inventory_movements`). It replaced a static
 * demonstration that showed seven hard-coded events of a test shop to every merchant.
 *
 * No sample data and no placeholder: a shop without history shows « Aucune activité récente », nothing more.
 */
export function RecentActivity(): ReactElement {
  const { t, locale } = useI18n();
  const query = useRecentActivityQuery();

  return (
    <section aria-labelledby="recent-activity-title">
      <h2 id="recent-activity-title" className="mb-3 font-display text-lg font-semibold text-ink">
        {t('merchant.activity.title')}
      </h2>

      {query.isPending ? (
        <TableSkeleton columns={2} />
      ) : query.isError ? (
        <EmptyState
          title={t('merchant.activity.loadError')}
          description={t(resolveErrorMessageKey(query.error))}
        />
      ) : (
        <ActivityList events={query.data.events} t={t} locale={locale} />
      )}
    </section>
  );
}

function ActivityList({
  events,
  t,
  locale,
}: {
  readonly events: readonly ActivityEvent[];
  readonly t: I18nContextValue['t'];
  readonly locale: I18nContextValue['locale'];
}): ReactElement {
  const lines = events.flatMap((event) => {
    const line = describeActivity(event, (key) => t(key));
    return line === null ? [] : [{ event, line }];
  });

  if (lines.length === 0) {
    return <EmptyState title={t('merchant.activity.empty')} />;
  }

  return (
    <ol className="divide-y divide-lineSoft rounded-xl2 border border-line bg-surface shadow-card">
      {lines.map(({ event, line }) => (
        <li key={event.id} className="flex flex-col gap-0.5 px-4 py-3 sm:flex-row sm:gap-4">
          <time
            dateTime={event.at}
            className="shrink-0 text-xs tabular-nums text-inkFaint sm:w-36 sm:pt-0.5"
          >
            {formatActivityTime(event.at, locale)}
          </time>
          <div className="min-w-0">
            <p className="text-sm text-ink">{t(line.key, line.params)}</p>
            {event.actor !== null ? (
              <p className="text-xs text-inkFaint">{t(ACTOR_KEY[event.actor])}</p>
            ) : null}
            {event.reason !== null && event.reason !== '' ? (
              <p className="text-xs text-inkSoft">
                {t('merchant.activity.reason', { reason: event.reason })}
              </p>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

const ACTOR_KEY = {
  system: 'merchant.activity.actor.system',
  customer: 'merchant.activity.actor.customer',
  merchant: 'merchant.activity.actor.merchant',
} as const;
